import { useSyncExternalStore } from "react";

export const CART_STORAGE_KEY = "sugalumps_cart";

const listeners = new Set();
let currentSnapshot = null;

function readStorage() {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(CART_STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStorage(items) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);

  function onStorage(event) {
    if (event.key === CART_STORAGE_KEY) {
      currentSnapshot = readStorage().map(normalizeLine);
      listener();
    }
  }

  window.addEventListener("storage", onStorage);

  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

function normalizeLine(line) {
  const unitPrice = Number(line.unit_price ?? line.price ?? 0);
  const quantity = Math.max(1, Number(line.quantity || 1));

  return {
    ...line,
    id: String(line.id),
    quantity,
    unit_price: Number.isFinite(unitPrice) ? unitPrice : 0,
    label: line.label || line.item_name_snapshot || line.menu_item_name_snapshot || "Menu item",
    item_name_snapshot: line.item_name_snapshot || line.menu_item_name_snapshot || line.label || "Menu item",
    variant_name_snapshot: line.variant_name_snapshot || line.badge || "",
    flavors: Array.isArray(line.flavors) ? line.flavors : [],
    flavor_ids: Array.isArray(line.flavor_ids) ? line.flavor_ids : [],
    flavor_menu_item_ids: Array.isArray(line.flavor_menu_item_ids) ? line.flavor_menu_item_ids : [],
  };
}

function snapshot() {
  if (!currentSnapshot) {
    currentSnapshot = readStorage().map(normalizeLine);
  }

  return currentSnapshot;
}

function setSnapshot(items) {
  currentSnapshot = items.map(normalizeLine);
  writeStorage(currentSnapshot);
}

export function addCartItem(line) {
  setSnapshot([...snapshot(), normalizeLine(line)]);
}

export function removeCartItem(id) {
  setSnapshot(snapshot().filter((item) => item.id !== String(id)));
}

export function updateCartItemQuantity(id, amount) {
  setSnapshot(
    snapshot().map((item) =>
      item.id === String(id)
        ? { ...item, quantity: Math.max(1, item.quantity + amount) }
        : item,
    ),
  );
}

export function replaceCartItems(items) {
  setSnapshot(items);
}

export function reconcileInvalidCartItems(invalidItems) {
  const invalidRows = Array.isArray(invalidItems) ? invalidItems : [];
  if (!invalidRows.length) return snapshot();

  const removeIndexes = new Set(
    invalidRows
      .filter((entry) => entry.action !== "remove_add_ons")
      .map((entry) => Number(entry.index)),
  );

  const addOnRemovals = new Map();
  invalidRows
    .filter((entry) => entry.action === "remove_add_ons")
    .forEach((entry) => {
      addOnRemovals.set(Number(entry.index), new Set(entry.ids || []));
    });

  const nextItems = snapshot()
    .filter((_, index) => !removeIndexes.has(index))
    .map((item, index) => {
      const removedAddOns = addOnRemovals.get(index);
      if (!removedAddOns) return item;
      return {
        ...item,
        add_on_ids: (item.add_on_ids || []).filter((id) => !removedAddOns.has(id)),
        add_ons: (item.add_ons || []).filter((addOn) => !removedAddOns.has(addOn.id || addOn.add_on_id)),
      };
    });

  setSnapshot(nextItems);
  return nextItems;
}

export function clearCart() {
  setSnapshot([]);
}

export function useCart() {
  const items = useSyncExternalStore(subscribe, snapshot, () => []);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce((sum, item) => sum + item.unit_price * item.quantity, 0);

  return {
    items,
    itemCount,
    lineCount: items.length,
    subtotal,
    addItem: addCartItem,
    removeItem: removeCartItem,
    updateQuantity: updateCartItemQuantity,
    clear: clearCart,
    replaceItems: replaceCartItems,
    reconcileInvalidItems: reconcileInvalidCartItems,
  };
}
